<template>
  <el-dialog
    v-model="visible"
    :title="(doc && doc.name ? doc.name : '资料') + ' - 查看/上传'"
    width="80%"
    :close-on-click-modal="false"
  >
    <div class="doc-body">
      <div v-if="files.length" class="doc-preview">
        <p class="doc-subtitle">当前文件列表</p>
        <el-table :data="files" border style="width: 100%; margin-top: 8px;">
          <el-table-column prop="fileName" label="文件名" min-width="240">
            <template #default="{ row }">
              <span class="file-item">
                <span class="file-ext">{{ getFileExtension(row.fileName) }}</span>
                <span>{{ row.fileName }}</span>
              </span>
            </template>
          </el-table-column>
          <el-table-column label="文件类型" width="120">
            <template #default="{ row }">{{ getFileExtension(row.fileName) }}</template>
          </el-table-column>
          <el-table-column label="上传时间" width="180">
            <template #default="{ row }">{{ formatDate(row.uploadTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="right">
            <template #default="{ row }">
              <button class="dt-op" @click="$emit('download', row.url, row.fileName)">下载</button>
              <span class="dt-sep">·</span>
              <button class="dt-op is-danger" @click="$emit('delete-file', row.id)">删除</button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <p v-else class="doc-empty">暂无相关文件</p>

      <div class="doc-upload">
        <el-upload
          ref="uploadRef"
          :action="uploadUrl"
          :headers="uploadHeaders"
          :data="{ storeEntCode, documentType: doc && doc.type }"
          :on-success="onSuccess"
          :on-error="onError"
          :before-upload="beforeUpload"
          :file-list="fileList"
          :auto-upload="false"
          multiple
        >
          <button type="button" class="btn" @click="pickFile">选择文件</button>
          <template #tip>
            <p class="doc-tip">
              支持 Word(doc,docx)、Excel(xls,xlsx)、PDF(pdf)、图片(jpg,png,jpeg)、文本(txt) 等格式<br>
              单文件不超过 10MB，最多选择 10 个文件
            </p>
          </template>
        </el-upload>

        <button
          class="btn btn-primary"
          style="margin-top: 10px;"
          :disabled="!fileList.length"
          @click="submitUpload"
        >上传文件</button>
      </div>
    </div>

    <template #footer>
      <button class="btn" @click="visible = false">关闭</button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed } from 'vue'
import { formatDate } from './formatters'

export default {
  name: 'StoreDocumentDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    doc: { type: Object, default: () => ({}) },
    storeEntCode: { type: String, default: '' },
    uploadUrl: { type: String, default: '' },
    uploadHeaders: { type: Object, default: () => ({}) },
    fileList: { type: Array, default: () => [] },
    beforeUpload: { type: Function, default: null }
  },
  emits: ['update:modelValue', 'update:fileList', 'download', 'delete-file', 'upload-success', 'upload-error'],
  setup(props, { emit }) {
    const uploadRef = ref(null)

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    const files = computed(() => (props.doc && Array.isArray(props.doc.files) ? props.doc.files : []))

    const getFileExtension = (fileName) => {
      if (!fileName) return '未知类型'
      const ext = fileName.split('.').pop().toUpperCase()
      return ext || '未知类型'
    }

    // el-upload 的 file-list 为受控属性，需手动同步回父组件
    const syncFiles = (list) => emit('update:fileList', list)

    const pickFile = () => {
      const input = uploadRef.value?.$el?.querySelector('input[type=file]')
      if (input) input.click()
    }

    const submitUpload = () => {
      uploadRef.value?.submit()
    }

    const onSuccess = (response, file, list) => {
      syncFiles(list || [])
      emit('upload-success', response)
    }

    const onError = (error, file, list) => {
      syncFiles(list || [])
      emit('upload-error', error)
    }

    return {
      uploadRef, visible, files,
      getFileExtension, pickFile, submitUpload, onSuccess, onError, formatDate
    }
  }
}
</script>

<style scoped>
.doc-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.doc-subtitle {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.doc-empty {
  margin: 0;
  padding: 28px 0;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-control);
}

.file-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.file-ext {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  padding: 1px 5px;
  border-radius: var(--radius-tag);
  background: var(--bg-page);
  color: var(--text-secondary);
  font-size: 10px;
}

.doc-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.6;
}

.dt-op {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--brand-500);
  cursor: pointer;
}

.dt-op:hover {
  text-decoration: underline;
}

.dt-op.is-danger {
  color: var(--danger-text);
}

.dt-sep {
  color: var(--text-disabled);
  margin: 0 5px;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
